import CheckIcon from '@mui/icons-material/Check';
import { TransferRateDto } from 'common/api/dto/rates.dto';
import { WizardNumericInput } from 'common/components/WizardControls/WizardNumericInput';
import currency from 'currency.js';
import { useCallback, useMemo, useState } from 'react';
import { UseFormWatch } from 'react-hook-form';
import { Column } from 'react-table';
import {
  CenteredHeader,
  CenteredText,
} from '../components/Sub-views/RatesView/Tabs/styles';
import { RateFormProps } from '../components/Sub-views/RatesView/Tabs/TableRateInput';
import { TransferRateEdit } from '../components/Sub-views/RatesView/Tabs/TransferRates';
import { createRateKey } from '../components/Sub-views/RatesView/utils/functions';

export type TransferRateTableRows = {
  range: string;
  chargeTarps: boolean;
  route: string;
  rate: RateFormProps & { groupKey: string };
};

export const useTransferRatesTableData: {
  (
    rows: TransferRateDto[],
    watch: UseFormWatch<TransferRateEdit>,
    isSubmitting: boolean,
  ): {
    columns: Column<TransferRateTableRows>[];
    data: TransferRateTableRows[];
    isEditing: boolean;
    setIsEditing: (isEditing: boolean) => void;
  };
} = (rows, watch, isSubmitting) => {
  const [isEditing, setIsEditing] = useState(false);

  const RateInputWrapper = useCallback(
    ({ value }: { value: RateFormProps & { groupKey: string } }) => {
      const { formValueKey, displayValue, groupKey } = value;
      const watchedValue = watch(groupKey);

      return isEditing ? (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {formValueKey === groupKey ? (
            <WizardNumericInput
              maxWidth='100px'
              maxHeight='22px'
              name={formValueKey}
              isDisabled={isSubmitting}
            />
          ) : (
            <CenteredText>{watchedValue}</CenteredText>
          )}
        </div>
      ) : (
        <CenteredText>{displayValue}</CenteredText>
      );
    },
    [isEditing, isSubmitting, watch],
  );

  const columns: Column<TransferRateTableRows>[] = useMemo(
    () => [
      {
        Header: () => <CenteredHeader>Route</CenteredHeader>,
        accessor: 'route',
        width: '33%',
        Cell: ({ value }) => <CenteredText>{value}</CenteredText>,
      },
      {
        Header: () => <CenteredHeader>Tarps</CenteredHeader>,
        accessor: 'chargeTarps',
        width: '5%',
        Cell: ({ value }) => (
          <CenteredText>{value && <CheckIcon />}</CenteredText>
        ),
      },
      {
        Header: () => <CenteredHeader>Range</CenteredHeader>,
        accessor: 'range',
        width: '32%',
        Cell: ({ value }) => <CenteredText>{value}</CenteredText>,
      },
      {
        Header: () => <CenteredHeader>Transfer Rate</CenteredHeader>,
        accessor: 'rate',
        width: '30%',
        Cell: ({ value }) => <RateInputWrapper value={value} />,
      },
    ],
    [RateInputWrapper],
  );

  const data: TransferRateTableRows[] = useMemo(() => {
    const group = { rangeId: -1, key: '' };

    return rows.map(({ id, rangeId, route, chargeTarps, range, rate }) => {
      const formValueKey = createRateKey(rangeId, id);

      // Many rows belong to the same mileage range, meaning the same rate
      // applies to all. To avoid needlessly making the user edit all rows,
      // we only make the first row editable, and apply the change to the rest.
      // We do this by looking at the group key set here.
      if (group.rangeId !== rangeId) {
        group.rangeId = rangeId;
        group.key = formValueKey;
      }

      return {
        route,
        chargeTarps,
        range: `${range} miles`,
        rate: {
          formValueKey,
          displayValue: currency(rate).format(),
          groupKey: group.key,
        },
      };
    });
  }, [rows]);

  return {
    columns,
    data,
    isEditing,
    setIsEditing,
  };
};
